import React, { useContext, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ActionIcon, Button, Menu } from '@mantine/core';
import dayjs from 'dayjs';
import { Dots, Plus, Trash } from 'tabler-icons-react';
import { Context as AdminContext } from '../../../providers/AdminContextProvider';
import TableView from '../../common/TableView';
import { useModalState, usePaginationFilter } from '../../../helpers/hooks';
import HeaderView from '../../common/HeaderView';
import { triggerNotification } from '../../../helpers/notification';
import AppStack from '../../common/AppStack';
import { formatUtcDate } from '../../../helpers/format';
import AppMenu from '../../common/AppMenu';
import { VIEW_ACTIONS_ENUM } from '../../../config/constants';
import ConfirmModal from '../../common/ConfirmModal';
import AppText from '../../common/AppText';

const TABLE_COLUMNS = [
  {
    label: 'Title',
    value: 'name',
    sortable: true
  },
  {
    label: 'Store content',
    value: 'store',
    sortable: true
  },
  {
    label: 'Created At',
    value: 'created',
    sortable: true
  }
];

const AdminStockContentTableView = () => {
  const hasFetched = useRef(false);
  const navigate = useNavigate();
  const {
    state,
    fetchPaginatedEcomStockContent,
    deleteEcomStockContent
  } = useContext(AdminContext);
  const [selectedRows, setSelectedRows] = useState([]);
  const {
    pageIndex,
    totalCount,
    totalPages,
    totalNoFilterCount,
    data,
    filter,
    loading: dataLoading,
    isDescendingSort,
    onPageChange,
    onFilterChange,
    onSortChange,
    onRefresh,
    onReset
  } = usePaginationFilter(
    { search: '' },
    (newFilter) => {
      setSelectedRows([]);
      fetchPaginatedEcomStockContent(newFilter, null, (error) => {
        triggerNotification(error);
      });
    },
    state.paginatedEcomStockContent
  );
  const {
    state: modalState,
    onOpenModal,
    onCloseModal,
    onChangeModalLoading
  } = useModalState();

  const loading = !hasFetched.current || dataLoading;

  const selectedStockContent = data.filter((f) =>
    selectedRows.includes(f.pkEcomStockContent)
  );

  useEffect(() => {
    onReset();
    hasFetched.current = true;
  }, []);

  return (
    <HeaderView
      breadcrumbs={[
        {
          title: 'Stock content',
          to: '/admin/stock-content'
        }
      ]}
      rightSection={
        !loading && (
          <Button
            color="dark"
            component={Link}
            leftSection={<Plus size={18} />}
            radius="md"
            size="compact-md"
            style={{ fontSize: 14 }}
            to="/admin/stock-content/create"
          >
            Create new
          </Button>
        )
      }
    >
      <TableView
        columns={TABLE_COLUMNS}
        emptyMessage="No stock content found."
        enableInputsWhenLoading
        filters={[
          {
            key: 1,
            label: 'Search',
            placeholder: 'Filter by name',
            value: filter.search || '',
            onChange: (search) =>
              onFilterChange({
                ...filter,
                search
              })
          }
        ]}
        isDescendingSort={isDescendingSort}
        isLoading={loading}
        noSort
        onChangeSortBy={onSortChange}
        onPageChange={onPageChange}
        onRefresh={onRefresh}
        onSelect={setSelectedRows}
        pageIndex={pageIndex}
        rows={data.map((p) => ({
          key: p.pkEcomStockContent,
          columns: [
            {
              key: 1,
              label: p.name,
              weight: 500,
              component: Link,
              to: `/admin/stock-content/${p.pkEcomStockContent}`
            },
            {
              key: 2,
              label: p.ecomStore?.name || 'N/A',
              color: p.ecomStore?.name ? '#000' : '#C40000',
              subLabel: p.ecomStore?.domain || null
            },
            {
              key: 3,
              label: dayjs(formatUtcDate(p.createdAt)).format('MMM D, YYYY')
            }
          ]
        }))}
        selectActionSection={
          <AppMenu
            control={
              <ActionIcon color="#ced4da" size="lg" variant="outline">
                <Dots color="#000" />
              </ActionIcon>
            }
            styles={{ dropdown: { padding: 0 } }}
          >
            <AppStack style={{ padding: 2, gap: 1 }}>
              <Menu.Item
                leftSection={<Trash size={18} />}
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onOpenModal(VIEW_ACTIONS_ENUM.DELETE, [
                    ...selectedStockContent
                  ]);
                }}
                style={{ color: 'red' }}
              >
                Delete products
              </Menu.Item>
            </AppStack>
          </AppMenu>
        }
        selectedRows={selectedStockContent.map((p) => p.pkEcomStockContent)}
        sortBy={filter.sort || TABLE_COLUMNS[0].value}
        tableTitle="Stock content"
        totalCount={totalCount}
        totalPages={totalPages}
      />

      <ConfirmModal
        confirmActionColor="red"
        confirmActionText="Yes, delete content"
        isLoading={modalState.loading}
        isOpen={
          modalState.isOpen && modalState.action === VIEW_ACTIONS_ENUM.DELETE
        }
        message={
          <AppStack style={{ gap: 10 }}>
            <AppText style={{ fontSize: 16 }}>
              Are you sure you want to delete{' '}
              <b>
                {modalState.item?.length === 1
                  ? modalState.item[0]?.name
                  : `${modalState.item?.length} stock content`}
              </b>
              ?
            </AppText>
            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
              This action cannot be undone.
            </AppText>
          </AppStack>
        }
        onCancel={onCloseModal}
        onConfirm={() => {
          onChangeModalLoading(true);
          deleteEcomStockContent(
            modalState.item.map((i) => i.pkEcomStockContent),
            () => {
              onRefresh();
              triggerNotification('Stock content deleted.', 'success');
              onCloseModal();
            },
            (error) => {
              triggerNotification(error);
              onChangeModalLoading(false);
            }
          );
        }}
        title={`Delete ${
          modalState.item?.length === 1
            ? modalState.item[0]?.name
            : `${modalState.item?.length} products`
        }?`}
      />
    </HeaderView>
  );
};

export default AdminStockContentTableView;
